import React, { JSX, useRef, useState } from 'react';

import Papa from 'papaparse';
import fileDownload from 'react-file-download';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useBulkUpdateCategories } from '~/hooks/apis/experiences/useBulkUpdateCategories';

import {
  BulkCategoryRow,
  CategoryItem,
  UncategorisedOffer,
  getUncategorisedOffers,
} from '~/services/ExperiencesService';

import { generateCategoryTemplate } from '~/utils/generateCategoryTemplate';
import { parseExperienceCategoryFile } from '~/utils/parseExperienceCategoryFile';

type View = 'idle' | 'preview' | 'results';

type ParsedRow = BulkCategoryRow & { id: string; rowError?: string };

type ResultRow = {
  id: string;
  offerId: string;
  status: 'updated' | 'failed';
  reason?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  categories: CategoryItem[];
};

export default function BulkCategoryUpdateDialog({ open, onClose, categories }: Props): JSX.Element {
  const [view, setView] = useState<View>('idle');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseErrors, setParseErrors] = useState<Array<{ row: number; message: string }>>([]);
  const [resultRows, setResultRows] = useState<ResultRow[]>([]);
  const [downloadingUncategorised, setDownloadingUncategorised] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mutation = useBulkUpdateCategories();

  const hasErrors = parseErrors.length > 0;

  const handleClose = () => {
    setView('idle');
    setParsedRows([]);
    setParseErrors([]);
    setResultRows([]);
    mutation.reset();
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const { rows, errors } = await parseExperienceCategoryFile(file);
      const withIds: ParsedRow[] = rows.map((r, i) => ({ ...r, id: String(i) }));

      if (errors.length > 0) {
        // Merge error rows as placeholder rows so the user sees them in the grid
        const errorOnlyRows: ParsedRow[] = errors.map((err, i) => ({
          id: `err-${i}`,
          offerId: `Row ${err.row}`,
          categories: [],
          action: 'add' as const,
          rowError: err.message,
        }));
        setParsedRows([...withIds, ...errorOnlyRows]);
      } else {
        setParsedRows(withIds);
      }

      setParseErrors(errors);
      setView('preview');
    } catch (err) {
      setParseErrors([{ row: 0, message: err instanceof Error ? err.message : String(err) }]);
      setView('preview');
    }
  };

  const handleSubmit = async () => {
    const validRows = parsedRows
      .filter((r) => !r.rowError)
      .map(({ offerId, categories, action }) => ({ offerId, categories, action }));
    const result = await mutation.mutateAsync(validRows);

    const updatedSet = new Set(result.updated);
    const failedMap = new Map(result.failed.map((f) => [f.offerId, f.reason]));

    const rows: ResultRow[] = validRows.map((r, i) => ({
      id: String(i),
      offerId: r.offerId,
      status: updatedSet.has(r.offerId) ? 'updated' : 'failed',
      reason: failedMap.get(r.offerId),
    }));

    setResultRows(rows);
    setView('results');
  };

  const handleDownloadUncategorised = async () => {
    setDownloadingUncategorised(true);
    try {
      const offers: UncategorisedOffer[] = await getUncategorisedOffers();
      const rows = offers.map((o) => ({ 'DEAL ID': o.id, Title: o.title, Brands: o.brands.join(', ') }));
      fileDownload(Papa.unparse(rows), 'uncategorised-offers.csv');
    } finally {
      setDownloadingUncategorised(false);
    }
  };

  const previewColumns: GridColDef<ParsedRow>[] = [
    { field: 'offerId', headerName: 'Deal ID', flex: 1 },
    {
      field: 'categories',
      headerName: 'Category Tags',
      flex: 2,
      renderCell: ({ value, row }) =>
        row.rowError ? (
          <Typography variant="body2" color="error">
            {row.rowError}
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {(value as string[]).map((c) => (
              <Chip key={c} label={c} size="small" />
            ))}
          </Stack>
        ),
    },
    { field: 'action', headerName: 'Action', width: 100 },
  ];

  const resultColumns: GridColDef<ResultRow>[] = [
    { field: 'offerId', headerName: 'Deal ID', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) =>
        value === 'updated' ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            <Typography variant="body2" color="success.main">
              Updated
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ErrorOutlineIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error">
              Failed
            </Typography>
          </Stack>
        ),
    },
    { field: 'reason', headerName: 'Reason', flex: 2 },
  ];

  const updatedCount = resultRows.filter((r) => r.status === 'updated').length;
  const failedCount = resultRows.filter((r) => r.status === 'failed').length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Update Categories</DialogTitle>

      <DialogContent>
        {view === 'idle' && (
          <Stack spacing={2} mt={1}>
            <Stack direction="row" spacing={2}>
              <Button variant="text" onClick={() => generateCategoryTemplate(categories)}>
                Download Template
              </Button>
              <Button
                variant="text"
                onClick={handleDownloadUncategorised}
                disabled={downloadingUncategorised}
                startIcon={downloadingUncategorised ? <CircularProgress size={16} /> : undefined}
              >
                Download Uncategorised Offers
              </Button>
            </Stack>
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button variant="contained" onClick={() => fileInputRef.current?.click()}>
                Upload spreadsheet
              </Button>
            </Box>
          </Stack>
        )}

        {view === 'preview' && (
          <Stack spacing={2} mt={1}>
            {hasErrors && (
              <Alert severity="error">
                {parseErrors.length} row{parseErrors.length !== 1 ? 's have' : ' has'} errors — fix the file and
                re-upload.
              </Alert>
            )}
            {mutation.isError && (
              <Alert severity="error">
                Submission failed: {mutation.error instanceof Error ? mutation.error.message : 'Unknown error'}
              </Alert>
            )}
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={parsedRows}
                columns={previewColumns}
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
              />
            </Box>
          </Stack>
        )}

        {view === 'results' && (
          <Stack spacing={2} mt={1}>
            <Alert severity={failedCount === 0 ? 'success' : 'warning'}>
              {updatedCount} updated, {failedCount} failed
            </Alert>
            <Box sx={{ height: 400 }}>
              <DataGrid rows={resultRows} columns={resultColumns} disableRowSelectionOnClick />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {view === 'idle' && <Button onClick={handleClose}>Close</Button>}

        {view === 'preview' && (
          <>
            <Button
              variant="text"
              onClick={() => {
                setView('idle');
                setParsedRows([]);
                setParseErrors([]);
                mutation.reset();
              }}
            >
              Re-upload
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={hasErrors || mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              Submit
            </Button>
          </>
        )}

        {view === 'results' && <Button onClick={handleClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
}
